/**
 * Ownership mutation hooks (SCRUM-172) — open / transfer / end / amend
 * share for the Property detail Owner tab.
 *
 * Request payloads use the canonical Zod-inferred input types from
 * `@artemis/validators` (CLAUDE.md frontend rule 4 — validators are the
 * single source of truth; never redefine locally). The OpenAPI request
 * bodies are typed generically (`Object`) because the BE didn't annotate
 * the request DTOs, so the typed payload is cast to the client's body
 * slot at the call seam — drop the cast when the BE annotates the
 * request schemas and `@artemis/contracts` regenerates.
 *
 * Optimistic concurrency: end / transfer / amend-share REQUIRE an
 * `If-Match` strong entity-tag (`"<version>"`, per the BE's `mintETag`)
 * — the BE 400s without it. The version comes from the current
 * `OwnershipView.version`. `open` creates the first ownership and takes
 * no If-Match. Mirrors the PATCH pattern in `use-update-property-status.ts`.
 *
 * On success each hook invalidates the property's `ownership`,
 * `ownershipHistory`, and `detail` query keys so the Owner tab + hero
 * refetch the fresh row.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  OpenOwnershipInput,
  TransferOwnershipInput,
  EndOwnershipInput,
  AmendOwnershipShareInput,
} from '@artemis/validators';
import { apiClient } from '../../../shared/api-client.js';
import { useCurrentOrg } from '../../../shared/hooks/useCurrentOrg.js';
import { propertyQueryKeys } from './queryKeys.js';

/** RFC 7232 strong entity-tag for the ownership version (BE `mintETag`). */
function etag(version: number): string {
  return `"${version}"`;
}

/** Friendly message for the inline `role="alert"` in each modal. */
function messageFor(status: number): string {
  if (status === 409 || status === 412) {
    return 'This ownership changed since you opened it. Refresh and try again.';
  }
  if (status === 403) return 'You don’t have permission to change ownership.';
  return 'Something went wrong saving the change. Try again.';
}

interface MutationApi<TVars> {
  readonly mutateAsync: (vars: TVars) => Promise<void>;
  readonly status: 'idle' | 'pending' | 'success' | 'error';
  readonly errorMessage: string | null;
  readonly reset: () => void;
}

function useOrgSlug(): string | null {
  const orgState = useCurrentOrg();
  return orgState?.membership.organizationSlug ?? null;
}

/** Shared success-side invalidation across all ownership mutations. */
function useOwnershipInvalidation(): (propertyId: string) => Promise<void> {
  const queryClient = useQueryClient();
  return async (propertyId: string) => {
    await queryClient.invalidateQueries({
      queryKey: propertyQueryKeys.ownership(propertyId),
    });
    await queryClient.invalidateQueries({
      queryKey: propertyQueryKeys.ownershipHistory(propertyId),
    });
    await queryClient.invalidateQueries({
      queryKey: propertyQueryKeys.detail(propertyId),
    });
  };
}

function errorMessageOf(
  mutation: { status: string; error: Error | null },
): string | null {
  if (mutation.status !== 'error' || mutation.error === null) return null;
  const e = mutation.error as Error & { httpStatus?: number };
  return e.httpStatus !== undefined ? messageFor(e.httpStatus) : messageFor(0);
}

function httpError(status: number): Error & { httpStatus: number } {
  const err = new Error(`ownership mutation failed (${status})`) as Error & {
    httpStatus: number;
  };
  err.httpStatus = status;
  return err;
}

// ---------------------------------------------------------------- open

export interface OpenOwnershipVariables {
  readonly propertyId: string;
  readonly body: OpenOwnershipInput;
}

export function useOpenOwnership(): MutationApi<OpenOwnershipVariables> {
  const orgSlug = useOrgSlug();
  const invalidate = useOwnershipInvalidation();
  const mutation = useMutation<undefined, Error, OpenOwnershipVariables>({
    mutationFn: async (vars) => {
      if (orgSlug === null) throw new Error('Cannot open ownership without an org slug');
      const result = await apiClient.POST(
        '/organizations/{slug}/properties/{propertyId}/ownership',
        {
          params: { path: { slug: orgSlug, propertyId: vars.propertyId } },
          // Untyped request body in OpenAPI (`Object`) — payload is the
          // canonical validators type; cast at the seam (see file header).
          body: vars.body as never,
        },
      );
      if (!result.response.ok) throw httpError(result.response.status);
      await invalidate(vars.propertyId);
      return undefined;
    },
  });
  return {
    mutateAsync: async (vars) => {
      await mutation.mutateAsync(vars);
    },
    status: mutation.status,
    errorMessage: errorMessageOf(mutation),
    reset: mutation.reset,
  };
}

// ------------------------------------------------------------ transfer

export interface TransferOwnershipVariables {
  readonly propertyId: string;
  /** Current ownership version for the required If-Match. */
  readonly currentVersion: number;
  readonly body: TransferOwnershipInput;
}

export function useTransferOwnership(): MutationApi<TransferOwnershipVariables> {
  const orgSlug = useOrgSlug();
  const invalidate = useOwnershipInvalidation();
  const mutation = useMutation<undefined, Error, TransferOwnershipVariables>({
    mutationFn: async (vars) => {
      if (orgSlug === null) throw new Error('Cannot transfer ownership without an org slug');
      const result = await apiClient.POST(
        '/organizations/{slug}/properties/{propertyId}/ownership/transfer',
        {
          params: {
            path: { slug: orgSlug, propertyId: vars.propertyId },
            header: { 'If-Match': etag(vars.currentVersion) },
          },
          body: vars.body as never,
        },
      );
      if (!result.response.ok) throw httpError(result.response.status);
      await invalidate(vars.propertyId);
      return undefined;
    },
  });
  return {
    mutateAsync: async (vars) => {
      await mutation.mutateAsync(vars);
    },
    status: mutation.status,
    errorMessage: errorMessageOf(mutation),
    reset: mutation.reset,
  };
}

// ----------------------------------------------------------------- end

export interface EndOwnershipVariables {
  readonly propertyId: string;
  readonly ownershipId: string;
  readonly version: number;
  readonly body: EndOwnershipInput;
}

export function useEndOwnership(): MutationApi<EndOwnershipVariables> {
  const orgSlug = useOrgSlug();
  const invalidate = useOwnershipInvalidation();
  const mutation = useMutation<undefined, Error, EndOwnershipVariables>({
    mutationFn: async (vars) => {
      if (orgSlug === null) throw new Error('Cannot end ownership without an org slug');
      const result = await apiClient.PATCH(
        '/organizations/{slug}/properties/{propertyId}/ownership/{ownershipId}/end',
        {
          params: {
            path: {
              slug: orgSlug,
              propertyId: vars.propertyId,
              ownershipId: vars.ownershipId,
            },
            header: { 'If-Match': etag(vars.version) },
          },
          body: vars.body as never,
        },
      );
      if (!result.response.ok) throw httpError(result.response.status);
      await invalidate(vars.propertyId);
      return undefined;
    },
  });
  return {
    mutateAsync: async (vars) => {
      await mutation.mutateAsync(vars);
    },
    status: mutation.status,
    errorMessage: errorMessageOf(mutation),
    reset: mutation.reset,
  };
}

// --------------------------------------------------------- amend share

export interface AmendShareVariables {
  readonly propertyId: string;
  readonly ownershipId: string;
  readonly version: number;
  readonly body: AmendOwnershipShareInput;
}

export function useAmendShare(): MutationApi<AmendShareVariables> {
  const orgSlug = useOrgSlug();
  const invalidate = useOwnershipInvalidation();
  const mutation = useMutation<undefined, Error, AmendShareVariables>({
    mutationFn: async (vars) => {
      if (orgSlug === null) throw new Error('Cannot amend share without an org slug');
      const result = await apiClient.PATCH(
        '/organizations/{slug}/properties/{propertyId}/ownership/{ownershipId}/share',
        {
          params: {
            path: {
              slug: orgSlug,
              propertyId: vars.propertyId,
              ownershipId: vars.ownershipId,
            },
            header: { 'If-Match': etag(vars.version) },
          },
          body: vars.body as never,
        },
      );
      if (!result.response.ok) throw httpError(result.response.status);
      await invalidate(vars.propertyId);
      return undefined;
    },
  });
  return {
    mutateAsync: async (vars) => {
      await mutation.mutateAsync(vars);
    },
    status: mutation.status,
    errorMessage: errorMessageOf(mutation),
    reset: mutation.reset,
  };
}
